import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { Divider } from '../../divider/divider';
import { Button } from '../../form/button/button';
import { IconArrowUp } from '../../icons/arrow-up';
import { GapSizeType } from '../../../../theme/type/gap-type';
import { createStableClassToken } from '../../../../utils/stable-class-token';

type PortfolioExpandExcludedProps = {
    excludedExpanded: boolean;
    setExcludedExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    excludedPortfolios: number;
    size?: 'medium' | 'large';
    gap?: GapSizeType;
    forceVisible?: boolean;
};

export const PortfolioExpandExcluded: React.FC<PortfolioExpandExcludedProps> = observer(
    ({ excludedExpanded, setExcludedExpanded, excludedPortfolios, size = 'medium', gap = 'medium', forceVisible }) => {
        const { t } = useTranslation();
        const theme = useTheme();
        const buttonSize = size === 'medium' ? 'vsmall' : 'small';
        const classToken = createStableClassToken('portfolio-expand-excluded', [size, excludedExpanded]);
        const buttonStates = theme.button.variant.default.outlined;

        const label = excludedExpanded
            ? t('layout.tables.portfolios.hideExcluded')
            : t('layout.tables.portfolios.showExcluded');

        return excludedPortfolios > 0 || forceVisible ? (
            <Divider gap={gap} variant={'dashed'} borderColor="button">
                <span className={classToken}>
                    <style>{`
                        .${classToken} .portfolio-expand-excluded-count {
                            background: ${buttonStates.base.icon.background};
                            color: ${buttonStates.base.icon.color};
                        }

                        .${classToken}:hover .portfolio-expand-excluded-count {
                            background: ${buttonStates.hover.icon.background};
                            color: ${buttonStates.hover.icon.color};
                        }
                    `}</style>
                    <Button
                        type="button"
                        variation="outlined"
                        variant="default"
                        size={buttonSize}
                        onClick={() => setExcludedExpanded((v) => !v)}
                    >
                        <span className="-mr-[0.8rem] inline-flex items-center gap-xsmall">
                            <span>{label}</span>
                            <span className="portfolio-expand-excluded-count inline-flex h-[2.6rem] min-w-[2.6rem] items-center justify-center rounded-full px-[0.8rem] text-xsmall font-bold leading-none transition-colors [&_i]:h-[1.2rem] [&_i]:w-[1.2rem] [&_i]:text-inherit">
                                {excludedExpanded ? <IconArrowUp /> : excludedPortfolios}
                            </span>
                        </span>
                    </Button>
                </span>
            </Divider>
        ) : null;
    },
);
